/**
 * Mastheads and the shared footer — the chrome every Bluebird page uses.
 *
 * The homepage and sub-pages keep their distinct mastheads, while every public
 * page now ends with the complete mapped, newsletter, and four-column footer.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link, useForm, usePage } from "@inertiajs/react";
import { A, Button, Wrap } from "./primitives";
import { color, font, maxWidth, radius, space, type } from "@/lib/design-tokens";
import { cta, nav, shop } from "@/lib/copy/shop";
import { footer as footerCopy } from "@/lib/copy/footer";
import { BOOKING_ENABLED } from "@/lib/booking";
import {
  newsletterConfirmationCopy,
  newsletterConfirmationFrom,
  rememberNewsletterSubscription,
  type NewsletterConfirmation,
} from "@/lib/newsletter";
import { LocationBlock } from "./LocationBlock";
import { GoogleRatingBadge } from "./GoogleReviews";
import { socialLinks } from "@/components/layout/navigation-config";

const NAV_LINK: React.CSSProperties = {
  color: color.white,
  padding: "14px 0",
};

function Brand({ asLink }: { asLink: boolean }) {
  // Text-only branding — Aaron's 2026-09-08 call: "lets skip the logo for now
  // ... lets use no logo here". Mark candidates live on the Paper "Logo
  // candidates" page for whenever that decision reopens.
  const inner = (
    <span>{shop.nameFirst}<span style={{ color: color.gold }}>{shop.nameSecond}</span></span>
  );
  const style: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: 800,
    fontSize: "18px",
    letterSpacing: ".04em",
    textTransform: "uppercase",
    color: color.white,
  };
  // On the homepage the wordmark is not a link — you are already there.
  return asLink ? (
    <Link href="/" className="bb-brand" style={style}>
      {inner}
    </Link>
  ) : (
    <div style={style}>{inner}</div>
  );
}

function NavLinks({ current }: { current?: string }) {
  return (
    <nav
      style={{
        display: "flex",
        gap: "20px",
        fontSize: "15px",
        fontWeight: 500,
      }}
    >
      {nav.map((item) => {
        const active = current === item.href;
        return (
          <A
            key={item.href}
            href={item.href}
            className="bb-navlink"
            aria-current={active ? "page" : undefined}
            style={{ ...NAV_LINK, color: active ? color.gold : color.white }}
          >
            {item.label}
          </A>
        );
      })}
    </nav>
  );
}

/**
 * The homepage masthead: wordmark, nav, and the two CTAs.
 *
 * The maintenance CTA opens the dedicated Cal.com event; the test-ride CTA
 * leads to `/book`, whose live scheduler is the primary path and whose contact
 * form remains the human fallback. Neither button claims this page itself has
 * confirmed a slot.
 */
export function HomeMasthead({ children }: { children: ReactNode }) {
  return (
    <div data-section="masthead" style={{ background: color.blue, color: color.white }}>
      <header
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `22px ${space.gutter}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px 24px",
        }}
      >
        <Brand asLink={false} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "clamp(18px,3vw,34px)",
            flexWrap: "wrap",
          }}
        >
          <NavLinks />
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Button
              href={cta.maintenanceHref}
              variant="ghost"
              style={{
                border: `1.5px solid rgba(255,255,255,.45)`,
                fontSize: "15px",
                padding: "14px 18px",
              }}
            >
              {cta.maintenanceLabel}
            </Button>
            <Button
              href={cta.testRideHref}
              variant="red"
              style={{ fontSize: "15px", padding: "15px 18px" }}
            >
              {cta.testRideLabel}
            </Button>
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

/** The sub-page masthead: wordmark, nav, no CTAs. */
export function SubMasthead({
  current,
  children,
  photo,
  photoAlt,
  photoWidths = [1200, 1800, 2600],
  image,
  focalPosition = "center",
}: {
  current: string;
  children: ReactNode;
  /**
   * A lifestyle photograph behind the masthead, as `<name>` in
   * public/images/place/ (needs 1200/1800/2600 renditions).
   *
   * The comp gives sub-pages a flat blue slab while the homepage gets a
   * photograph behind its scrim. On a page like /bikes or /savings that slab is
   * the first thing a visitor sees, and it reads as an empty header rather than
   * a deliberate one — the same scrim already exists here, so the only thing
   * missing was an image behind it.
   *
   * Omit it and the page keeps the comp's flat blue, which is right when no
   * truthful place or exact-model photograph exists for the masthead.
   */
  photo?: string;
  photoAlt?: string;
  /** Responsive source widths, ascending. Override when the source is narrower than 2600px. */
  photoWidths?: readonly number[];
  /** Arbitrary truthful photography, including an exact-model product scene. */
  image?: {
    src: string;
    srcSet?: string;
    sizes?: string;
    alt?: string;
  };
  /** Object-position used when the hero photograph is cropped. */
  focalPosition?: string;
}) {
  const largestPhotoWidth = photoWidths[photoWidths.length - 1];
  const backgroundImage = image || (photo ? {
    src: `/images/place/${photo}-${largestPhotoWidth}.jpg`,
    srcSet: photoWidths.map((width) => `/images/place/${photo}-${width}.jpg ${width}w`).join(", "),
    sizes: "100vw",
    alt: photoAlt || "",
  } : undefined);

  return (
    <div
      data-section="masthead"
      style={{ background: color.blue, color: color.white, position: backgroundImage ? "relative" : undefined }}
    >
      {backgroundImage && (
        <>
          <img
            src={backgroundImage.src}
            srcSet={backgroundImage.srcSet}
            sizes={backgroundImage.sizes || "100vw"}
            alt={backgroundImage.alt || ""}
            aria-hidden={backgroundImage.alt ? undefined : "true"}
            loading="eager"
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", objectPosition: focalPosition, display: "block",
            }}
          />
          {/* The comp's own scrim, so the nav and headline stay legible over a
              photograph. Heavier at the bottom, where the headline sits. */}
          <div
            style={{
              position: "absolute", inset: 0,
              background:
                "linear-gradient(to top, rgba(0,40,104,.95) 0%, rgba(0,40,104,.86) 45%, rgba(0,40,104,.55) 100%)",
            }}
          />
        </>
      )}
      <div style={{ position: backgroundImage ? "relative" : undefined }}>
      <header
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `22px ${space.gutter}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px 24px",
        }}
      >
        <Brand asLink />
        <div style={{ display: "flex", alignItems: "center", gap: "clamp(14px,2.5vw,28px)", flexWrap: "wrap" }}>
          <NavLinks current={current} />
          {/*
            THE TEST-RIDE CTA, WHICH THE COMP DOES NOT PUT ON SUB-PAGES.

            The comp's homepage masthead carries "Schedule maintenance" and
            "Schedule a test ride"; its sub-page masthead carries neither, so
            the port dropped them and every interior page lost the site's
            primary action. A visitor reading /bikes or /about — the pages where
            somebody decides they want to come in — had no way to act on it
            without scrolling to a footer.

            That is a real loss rather than a stylistic choice: the site's only
            conversion is a visit, and this is the button that produces one. So
            it is added deliberately and reported as a named deviation in
            script/design_diff.mjs rather than smuggled in.

            One button, not two: maintenance is a Service-page action, and two
            CTAs beside the nav wraps badly at tablet widths.
          */}
          <Button
            href={BOOKING_ENABLED ? "/book" : cta.testRideHref}
            variant="red"
            style={{ fontSize: "15px", padding: "12px 18px", whiteSpace: "nowrap" }}
          >
            {cta.testRideLabel}
          </Button>
        </div>
      </header>
      {children}
      </div>
    </div>
  );
}

/**
 * The sub-page hero: a headline whose second clause is gold, an intro, and
 * optionally one action.
 */
export function SubHero({
  line1,
  line2,
  intro,
  introMaxWidth = "460px",
  action,
  introMarginTop,
}: {
  line1: string;
  line2: string;
  intro: string;
  introMaxWidth?: string;
  action?: ReactNode;
  introMarginTop?: string;
}) {
  const inline = Boolean(action);
  return (
    <section
      style={{
        maxWidth,
        margin: "0 auto",
        padding: `clamp(48px,7vw,96px) ${space.gutter} clamp(48px,7vw,88px)`,
      }}
    >
      <h1
        style={{
          margin: 0,
          fontWeight: 800,
          lineHeight: 1.02,
          fontSize: type.subHero,
          letterSpacing: "-.015em",
          maxWidth: inline ? "900px" : "920px",
        }}
      >
        {line1}
        <span style={{ display: "block", marginTop: ".1em", color: color.gold }}>{line2}</span>
      </h1>
      {inline ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "24px clamp(32px,5vw,72px)",
            alignItems: "flex-end",
            marginTop: "clamp(28px,4vw,44px)",
          }}
        >
          <p
            style={{
              fontSize: type.heroBody,
              lineHeight: 1.55,
              maxWidth: introMaxWidth,
              margin: 0,
              color: color.onBlue,
            }}
          >
            {intro}
          </p>
          {action}
        </div>
      ) : (
        <p
          style={{
            fontSize: type.heroBody,
            lineHeight: 1.55,
            maxWidth: introMaxWidth,
            margin: `${introMarginTop || "clamp(24px,3vw,36px)"} 0 0`,
            color: color.onBlue,
          }}
        >
          {intro}
        </p>
      )}
    </section>
  );
}

/**
 * The one signup path shared by both footer variants. It reaches the existing
 * contact endpoint with enough context for the reply to be about the list.
 */
export function NewsletterSignup() {
  const { flash } = usePage();
  const serverConfirmation = newsletterConfirmationFrom(flash, "footer-newsletter");
  const [confirmed, setConfirmed] = useState<NewsletterConfirmation | null>(null);
  const shown = confirmed ?? serverConfirmation;
  const serverConfirmationKey = serverConfirmation && `${serverConfirmation.status}:${serverConfirmation.email}`;
  // A confirmation that arrived through a full reload (stale-deploy 409)
  // never ran onSuccess, so record the subscription here too.
  useEffect(() => {
    if (serverConfirmationKey) rememberNewsletterSubscription();
  }, [serverConfirmationKey]);
  const { data, setData, post, processing, errors, reset } = useForm({
    firstName: "Newsletter",
    lastName: "Subscriber",
    email: "",
    inquiryType: "newsletter",
    message: "Please add me to occasional shop updates.",
    source: "footer-newsletter",
    is_robot: "",
  });

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    post("/contact", {
      preserveScroll: true,
      onSuccess: (page) => {
        rememberNewsletterSubscription();
        const submitted = data.email.trim();
        reset();
        setConfirmed(
          newsletterConfirmationFrom(page.flash, "footer-newsletter") ??
            { status: "subscribed", email: submitted, source: "footer-newsletter" }
        );
        if (typeof gtag === "function") {
          try {
            gtag("event", "newsletter_signup");
          } catch {
            /* Analytics cannot block a successful signup. */
          }
        }
      },
    });
  };

  return (
    <div data-footer-newsletter="" style={{ background: color.blue, color: color.white, borderBottom: "1px solid rgba(255,255,255,.16)" }}>
      <div
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `clamp(36px,5vw,56px) ${space.gutter}`,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))",
          gap: `24px clamp(32px,5vw,64px)`,
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              fontWeight: 800,
              color: color.white,
              fontSize: type.footerLede,
              letterSpacing: "-.01em",
            }}
          >
            {footerCopy.newsletterHeading}
          </div>
          <p
            style={{
              margin: "8px 0 0",
              fontSize: "15px",
              lineHeight: 1.55,
              color: color.onBlue,
              maxWidth: "44ch",
            }}
          >
            {footerCopy.newsletterBody}
          </p>
        </div>
        <div>
          {shown ? (
            <div role="status" aria-live="polite" data-newsletter-confirmation={shown.status} style={{ color: color.white, fontWeight: 800, fontSize: "18px" }}>
              {newsletterConfirmationCopy(shown).heading}
              <div style={{ marginTop: "6px", color: color.onBlue, fontSize: "14px", fontWeight: 400, overflowWrap: "anywhere" }}>
                {newsletterConfirmationCopy(shown).body}
              </div>
            </div>
          ) : <form
            action="/contact"
            method="post"
            onSubmit={submit}
            style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
          >
            <input type="hidden" name="firstName" value={data.firstName} />
            <input type="hidden" name="lastName" value={data.lastName} />
            <input type="hidden" name="inquiryType" value={data.inquiryType} />
            <input type="hidden" name="message" value={data.message} />
            <input type="hidden" name="source" value={data.source} />
            <input type="text" name="is_robot" value={data.is_robot} onChange={(event) => setData("is_robot", event.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px" }} />
            <input
              type="email"
              required
              name="newsletterEmail"
              value={data.email}
              onChange={(event) => setData("email", event.target.value)}
              placeholder="you@email.com"
              aria-label="Email address"
              className="bb-input"
              style={{
                flex: 1,
                minWidth: "200px",
                fontFamily: "inherit",
                fontSize: "16px",
                color: color.white,
                background: "rgba(255,255,255,.08)",
                border: "1.5px solid rgba(255,255,255,.35)",
                borderRadius: radius.button,
                padding: "14px 16px",
                outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={processing}
              className="bb-btn bb-btn-gold"
              style={{
                fontFamily: "inherit",
                cursor: "pointer",
                border: 0,
                fontSize: "15px",
                padding: "15px 24px",
              }}
            >
              {processing ? "Adding…" : footerCopy.newsletterButton}
            </button>
            {errors.email && <div role="alert" style={{ width: "100%", color: color.white, fontSize: "14px" }}>{errors.email}</div>}
          </form>}
        </div>
      </div>
    </div>
  );
}

/**
 * Compatibility name for existing page imports. Every public page now renders
 * the same complete footer as the homepage; there is no separate slim variant.
 */
export function SlimFooter({ borderTop = false, showLocation = true }: { borderTop?: boolean; showLocation?: boolean }) {
  // Compatibility name for the sub-page templates. The rendered footer is
  // intentionally identical to HomeFooter; only the optional top rule differs.
  return <HomeFooter borderTop={borderTop} showLocation={showLocation} />;
}

const FOOTER_HEADING: React.CSSProperties = {
  fontWeight: 800,
  color: color.white,
  fontSize: "13px",
  letterSpacing: ".12em",
  textTransform: "uppercase",
};

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: ReadonlyArray<{ label: string; href: string }>;
}) {
  return (
    <div>
      <div style={FOOTER_HEADING}>{title}</div>
      <div style={{ display: "grid", gap: 0, marginTop: "14px", fontSize: "16px" }}>
        {links.map((l) => (
          <A
            key={l.label}
            href={l.href}
            className="bb-footer-link"
            style={{ color: color.onBlue, padding: "13px 0", display: "block" }}
          >
            {l.label}
          </A>
        ))}
      </div>
    </div>
  );
}

/**
 * The homepage footer: newsletter band, four columns, and a rule.
 *
 * The address is delivered to the shop through the existing validated contact
 * path. No external list provider is implied; the confirmation says only what
 * the application actually did.
 */
export function HomeFooter({ borderTop = false, showLocation = true }: { borderTop?: boolean; showLocation?: boolean } = {}) {
  return (
    <footer
      data-section="footer"
      data-home-footer=""
      style={{
        background: color.blue,
        color: color.footerText,
        borderTop: borderTop ? "1px solid rgba(255,255,255,.14)" : undefined,
      }}
    >
      {showLocation && <LocationBlock />}
      <NewsletterSignup />

      <div
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `clamp(48px,7vw,88px) ${space.gutter} clamp(32px,4vw,48px)`,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(240px,100%),1fr))",
          gap: `clamp(36px,5vw,64px) clamp(28px,4vw,56px)`,
        }}
      >
        <FooterColumn title={footerCopy.columns.shop.title} links={footerCopy.columns.shop.links} />
        <FooterColumn title={footerCopy.columns.bikes.title} links={footerCopy.columns.bikes.links} />
        <FooterColumn title={footerCopy.columns.comeIn.title} links={footerCopy.columns.comeIn.links} />
        <div>
          <div style={FOOTER_HEADING}>{footerCopy.whoWeAreTitle}</div>
          <p
            style={{
              margin: "20px 0 0",
              fontSize: "15.5px",
              lineHeight: 1.65,
              color: color.onBlue,
              maxWidth: "34ch",
            }}
          >
            {footerCopy.whoWeAreBody}
          </p>
          <div style={{ ...FOOTER_HEADING, marginTop: "26px" }}>{footerCopy.ourShopTitle}</div>
          <div
            style={{ marginTop: "14px", fontSize: "15.5px", lineHeight: 1.7, color: color.onBlue }}
          >
            <a
              href={shop.mapHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: color.onBlue }}
            >
              {shop.addressLine1}
              <br />
              {shop.addressLine2}
            </a>
          </div>
          <div
            style={{ marginTop: "14px", fontSize: "15.5px", lineHeight: 1.7, color: color.onBlue }}
          >
            <span style={{ color: color.white, fontWeight: 700 }}>{shop.hoursFooter}</span>
            <br />
            {shop.hoursClosed}
          </div>
        </div>
      </div>

      <Wrap style={{ padding: `0 ${space.gutter} 44px` }}>
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,.16)",
            paddingTop: "22px",
            display: "flex",
            flexWrap: "wrap",
            gap: "10px 32px",
            justifyContent: "space-between",
            fontSize: "13.5px",
          }}
        >
          <div
            style={{
              fontWeight: 800,
              color: color.white,
              textTransform: "uppercase",
              letterSpacing: ".04em",
            }}
          >
            {shop.nameFirst}
            <span style={{ color: color.gold }}>{shop.nameSecond}</span>
          </div>
          <GoogleRatingBadge variant="footer" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 18px" }}>
            <a
              href={shop.phoneHref}
              className="bb-underline-grow bb-underline-grow-white"
              style={{ color: color.onBlue, whiteSpace: "nowrap", padding: "4px 0" }}
            >
              {shop.phone}
            </a>
            <a
              href={shop.emailHref}
              className="bb-underline-grow bb-underline-grow-white"
              style={{ color: color.onBlue, whiteSpace: "nowrap", padding: "4px 0" }}
            >
              {shop.email}
            </a>
            {/* Every public page reaches the shop's socials from here. The
                homepage Instagram feed is a bonus, not the only door. */}
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="bb-underline-grow bb-underline-grow-white"
                style={{ color: color.onBlue, whiteSpace: "nowrap", padding: "4px 0" }}
              >
                {social.label}
              </a>
            ))}
          </div>
        </div>
      </Wrap>
    </footer>
  );
}

export { font };
